import type { SessionUser } from '@motoveo/shared';
import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client';

const NAV = [
  { to: '/', label: 'Dashboard' },
  { to: '/submissions', label: 'Submissions' },
  { to: '/dealers', label: 'Dealers' },
  { to: '/cockpit', label: 'Cockpit' },
  { to: '/liquidity', label: 'Liquidity' },
  { to: '/deals', label: 'Deals' },
  { to: '/intake', label: 'New intake' },
];

/** Shared operator shell: top bar with nav + the signed-in operator + logout. */
export function Layout({ user, children }: { user: SessionUser; children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate('/login');
    },
  });

  return (
    <div className="shell">
      <header className="topbar">
        <strong>Motoveo</strong>
        <span className="muted">deal-desk</span>
        <nav className="nav">
          {NAV.map((n) => {
            const active = n.to === '/' ? location.pathname === '/' : location.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} className={active ? 'nav-link active' : 'nav-link'}>
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="spacer" />
        <span className="muted">
          {user.email} · {user.role}
        </span>
        <button onClick={() => logout.mutate()}>Log out</button>
      </header>
      <main>{children}</main>
    </div>
  );
}
